import {
  addLineToShopifyCart,
  CartItem,
  createShopifyCart,
} from '@/lib/shopify';
import { normalizeCheckoutUrl } from '@/lib/checkoutUrl';

export interface RebuiltShopifyCart {
  cartId: string;
  checkoutUrl: string | null;
  items: CartItem[];
}

export function detachCartItems(items: CartItem[]): CartItem[] {
  return items.map((item) => ({ ...item, lineId: null }));
}

/**
 * Recreate an expired Shopify cart from the line items persisted in the
 * storefront. This keeps products, quantities, variants, selected models, and
 * custom line attributes instead of emptying the customer's visible cart.
 */
export async function rebuildShopifyCart(
  items: CartItem[]
): Promise<RebuiltShopifyCart | null> {
  if (items.length === 0) return null;

  const [firstItem, ...remainingItems] = detachCartItems(items);
  const created = await createShopifyCart(firstItem);
  const rebuiltItems: CartItem[] = [
    { ...firstItem, lineId: created.lineId },
  ];

  for (const item of remainingItems) {
    const added = await addLineToShopifyCart(created.cartId, item);
    if (!added.success || !added.lineId) {
      throw new Error('Shopify could not restore every cart item.');
    }
    rebuiltItems.push({ ...item, lineId: added.lineId });
  }

  return {
    cartId: created.cartId,
    checkoutUrl: created.checkoutUrl
      ? normalizeCheckoutUrl(created.checkoutUrl)
      : null,
    items: rebuiltItems,
  };
}
